import { OffsetRange } from '../core/offsetRange.js';
import { SequenceDiff, ITimeout, InfiniteTimeout, DateTimeout } from '../algorithms/diffAlgorithm.js';
import { DynamicProgrammingDiffing } from '../algorithms/dynamicProgrammingDiffing.js';
import { MyersDiffAlgorithm } from '../algorithms/myersDiffAlgorithm.js';
import {
	optimizeSequenceDiffs,
	extendDiffsToEntireWordIfAppropriate,
	removeShortMatches,
	removeVeryShortMatchingLinesBetweenDiffs,
	removeVeryShortMatchingTextBetweenLongDiffs,
} from './heuristicSequenceOptimizations.js';
import { LineSequence } from './lineSequence.js';
import { LinesSliceCharSequence, Range } from './linesSliceCharSequence.js';
import {
	LineRange,
	RangeMapping,
	LineRangeMapping,
	DetailedLineRangeMapping,
	LinesDiff,
	ArrayText,
	lineRangeMappingFromRangeMappings,
} from './rangeMapping.js';

export { DateTimeout, InfiniteTimeout, ITimeout } from '../algorithms/diffAlgorithm.js';
export { LineRange, RangeMapping, LineRangeMapping, DetailedLineRangeMapping, LinesDiff } from './rangeMapping.js';
export { Range, Position } from './linesSliceCharSequence.js';

/**
 * Options for the lines diff computer.
 */
export interface ILinesDiffComputerOptions {
	/**
	 * Maximum computation time in milliseconds. 0 means no timeout.
	 */
	maxComputationTimeMs: number;

	/**
	 * Whether to ignore leading and trailing whitespace.
	 */
	ignoreTrimWhitespace: boolean;

	/**
	 * Whether to compute moved text detection.
	 */
	computeMoves: boolean;

	/**
	 * Whether to extend diffs to subword boundaries.
	 */
	extendToSubwords: boolean;
}

/**
 * The main diff computer that computes detailed line diffs.
 */
export class DefaultLinesDiffComputer {
	private readonly _dynamicProgrammingDiffing = new DynamicProgrammingDiffing();
	private readonly _myersDiffingAlgorithm = new MyersDiffAlgorithm();

	computeDiff(originalLines: string[], modifiedLines: string[], options: ILinesDiffComputerOptions): LinesDiff {
		// Handle identical single line
		if (originalLines.length <= 1 && arraysEqual(originalLines, modifiedLines)) {
			return new LinesDiff([], [], false);
		}

		// Handle empty single line - treat as full replacement
		if (originalLines.length === 1 && originalLines[0].length === 0 || modifiedLines.length === 1 && modifiedLines[0].length === 0) {
			return new LinesDiff([
				new DetailedLineRangeMapping(
					new LineRange(1, originalLines.length + 1),
					new LineRange(1, modifiedLines.length + 1),
					[
						new RangeMapping(
							new Range(1, 1, originalLines.length, originalLines[originalLines.length - 1].length + 1),
							new Range(1, 1, modifiedLines.length, modifiedLines[modifiedLines.length - 1].length + 1),
						)
					]
				)
			], [], false);
		}

		const timeout = options.maxComputationTimeMs === 0 ? InfiniteTimeout.instance : new DateTimeout(options.maxComputationTimeMs);
		const considerWhitespaceChanges = !options.ignoreTrimWhitespace;

		// Create perfect hash for lines (trimmed)
		const perfectHashes = new Map<string, number>();
		function getOrCreateHash(text: string): number {
			let hash = perfectHashes.get(text);
			if (hash === undefined) {
				hash = perfectHashes.size;
				perfectHashes.set(text, hash);
			}
			return hash;
		}

		const originalLinesHashes = originalLines.map((l) => getOrCreateHash(l.trim()));
		const modifiedLinesHashes = modifiedLines.map((l) => getOrCreateHash(l.trim()));

		const sequence1 = new LineSequence(originalLinesHashes, originalLines);
		const sequence2 = new LineSequence(modifiedLinesHashes, modifiedLines);

		// Choose algorithm based on size
		const lineAlignmentResult = (() => {
			if (sequence1.length + sequence2.length < 1700) {
				// Use the improved algorithm for small files
				return this._dynamicProgrammingDiffing.compute(
					sequence1,
					sequence2,
					timeout,
					(offset1, offset2) =>
						originalLines[offset1] === modifiedLines[offset2]
							? modifiedLines[offset2].length === 0
								? 0.1
								: 1 + Math.log(1 + modifiedLines[offset2].length)
							: 0.99
				);
			}

			return this._myersDiffingAlgorithm.compute(
				sequence1,
				sequence2,
				timeout
			);
		})();

		let lineAlignments = lineAlignmentResult.diffs;
		let hitTimeout = lineAlignmentResult.hitTimeout;

		// Apply heuristics
		lineAlignments = optimizeSequenceDiffs(sequence1, sequence2, lineAlignments);
		lineAlignments = removeVeryShortMatchingLinesBetweenDiffs(sequence1, sequence2, lineAlignments);

		const alignments: RangeMapping[] = [];

		const scanForWhitespaceChanges = (equalLinesCount: number) => {
			if (!considerWhitespaceChanges) {
				return;
			}

			for (let i = 0; i < equalLinesCount; i++) {
				const seq1Offset = seq1LastStart + i;
				const seq2Offset = seq2LastStart + i;
				if (originalLines[seq1Offset] !== modifiedLines[seq2Offset]) {
					// This is because of whitespace changes, diff these lines
					const characterDiffs = this._refineDiff(originalLines, modifiedLines, new SequenceDiff(
						new OffsetRange(seq1Offset, seq1Offset + 1),
						new OffsetRange(seq2Offset, seq2Offset + 1),
					), timeout, considerWhitespaceChanges, options);
					for (const a of characterDiffs.mappings) {
						alignments.push(a);
					}
					if (characterDiffs.hitTimeout) {
						hitTimeout = true;
					}
				}
			}
		};

		let seq1LastStart = 0;
		let seq2LastStart = 0;

		for (const diff of lineAlignments) {
			const equalLinesCount = diff.seq1Range.start - seq1LastStart;

			scanForWhitespaceChanges(equalLinesCount);

			seq1LastStart = diff.seq1Range.endExclusive;
			seq2LastStart = diff.seq2Range.endExclusive;

			const characterDiffs = this._refineDiff(originalLines, modifiedLines, diff, timeout, considerWhitespaceChanges, options);
			if (characterDiffs.hitTimeout) {
				hitTimeout = true;
			}
			for (const a of characterDiffs.mappings) {
				alignments.push(a);
			}
		}

		scanForWhitespaceChanges(originalLines.length - seq1LastStart);

		const original = new ArrayText(originalLines);
		const modified = new ArrayText(modifiedLines);

		const changes = lineRangeMappingFromRangeMappings(alignments, original.lines, modified.lines);

		// TODO: Compute moves if options.computeMoves is true
		const moves: never[] = [];

		return new LinesDiff(changes, moves, hitTimeout);
	}

	private _refineDiff(
		originalLines: string[],
		modifiedLines: string[],
		diff: SequenceDiff,
		timeout: ITimeout,
		considerWhitespaceChanges: boolean,
		options: ILinesDiffComputerOptions
	): { mappings: RangeMapping[]; hitTimeout: boolean } {
		const lineRangeMapping = toLineRangeMapping(diff);
		const rangeMapping = lineRangeMapping.toRangeMapping2(originalLines, modifiedLines);

		const slice1 = new LinesSliceCharSequence(originalLines, rangeMapping.originalRange, considerWhitespaceChanges);
		const slice2 = new LinesSliceCharSequence(modifiedLines, rangeMapping.modifiedRange, considerWhitespaceChanges);

		const diffResult = slice1.length + slice2.length < 500
			? this._dynamicProgrammingDiffing.compute(slice1, slice2, timeout)
			: this._myersDiffingAlgorithm.compute(slice1, slice2, timeout);

		let diffs = diffResult.diffs;
		diffs = optimizeSequenceDiffs(slice1, slice2, diffs);
		diffs = extendDiffsToEntireWordIfAppropriate(slice1, slice2, diffs, (seq, idx) => seq.findWordContaining(idx));

		if (options.extendToSubwords) {
			diffs = extendDiffsToEntireWordIfAppropriate(slice1, slice2, diffs, (seq, idx) => seq.findSubWordContaining(idx), true);
		}

		diffs = removeShortMatches(slice1, slice2, diffs);
		diffs = removeVeryShortMatchingTextBetweenLongDiffs(slice1, slice2, diffs);

		const result = diffs.map(
			(d) =>
				new RangeMapping(
					slice1.translateRange(d.seq1Range),
					slice2.translateRange(d.seq2Range)
				)
		);

		return {
			mappings: result,
			hitTimeout: diffResult.hitTimeout,
		};
	}
}

function toLineRangeMapping(sequenceDiff: SequenceDiff) {
	return new LineRangeMapping(
		new LineRange(sequenceDiff.seq1Range.start + 1, sequenceDiff.seq1Range.endExclusive + 1),
		new LineRange(sequenceDiff.seq2Range.start + 1, sequenceDiff.seq2Range.endExclusive + 1),
	);
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
	if (a.length !== b.length) {
		return false;
	}
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) {
			return false;
		}
	}
	return true;
}
